/**
 * Iter51 Etapa 4 — SaleOverview (single-source mockup-faithful sale page).
 *
 * Layout 1:1 cu `crm_sale_detail_page.html`:
 *   1. Breadcrumb (Cazuri → Agenție → Cod dosar)
 *   2. Hero header — Avatar inițiale + Nume client + CNP/Județ/Cont + Editează/Contactează
 *   3. 3 metric pills (Status dosar / Commodity / Canal vânzare)
 *   4. 2-col grid: Date client + Detalii contract (iconițe per rând)
 *   5. Echipa de vânzare (3 avatare pe rând)
 *   6. Timeline vânzare (6 pași verticali)
 *   7. 2-col grid: Quality Call + Detalii proces
 *   8. Withdrawal banner / buton
 *   9. Action buttons jos (Istoric client / Exportă fișă / Acțiuni dosar)
 *
 * Inline edit toggle:
 *   - „Editează" comută isEditing=true → fiecare câmp editabil devine input/select
 *   - „Salvează" trimite PUT-uri paralele per entitate (client / contract / contract_status / sale)
 *   - „Anulează" face reset la draft
 */
import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  ChevronRight, IdCard, MapPin, FileText, Home, Building, Network, Hash, Tag,
  Plug, Store, User, Users, FileSignature, PhoneCall,
  Settings, Zap, Flame, History, Download, ArrowRight, Phone,
  Edit, Save, X, Workflow, Ban, Undo2, AlertTriangle,
  Route as RouteIcon, Calendar,
} from "lucide-react";
import SupplierCombobox from "./SupplierCombobox";
import AgentCombobox from "./AgentCombobox";
import { CopyableValue } from "./CopyableValue";
import { EmptyValue } from "./EmptyValue";
// Iter53 P32 — atoms extrași în submodul pentru reutilizare + reducere LOC
import {
  fmt, initials, truncateMid, PALETTES,
  FieldRow, SectionCard, PersonChip,
  SUPPLIER_STATUS_OPTIONS, NotesBoInline,
} from "./SaleOverview/atoms";
// Iter53 P34 — timeline + validity card extraşi în submodule (self-contained)
import { CrmActivityTimeline } from "./SaleOverview/CrmActivityTimeline";
import { StatusValidityCard, computeValidity } from "./SaleOverview/StatusValidityCard";
import { formatRoMonthYear } from "../lib/dates";
import { qcHelper } from "../lib/qcHelper";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =========================== legacy helpers (kept for backwards-compat) ===========================
const PREVIOUS_SUPPLIERS = ["PPC Energie S.A.", "ENGIE Romania", "E.ON Energie", "Electrica Furnizare", "Hidroelectrica", "Restart Energy", "CEZ Vânzare"];
const DISTRIBUTORS = ["Distribuție Energie Oltenia", "Distribuție Energie Electrica", "Delgaz Grid", "E-Distribuție Banat", "E-Distribuție Dobrogea", "E-Distribuție Muntenia"];

// Iter51 Etapa 14 — Tip produs catalog (also used in Offers admin)
// Iter52 P14h — Canonical form is LONG to match DB. Values must equal the
// exact `contract.product_kind` strings (no transformation in queries).
export const PRODUCT_KIND_OPTIONS = [
  { value: "EE",                 label: "EE",                badge: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "Gaz",                label: "Gaz",               badge: "bg-orange-100 text-orange-800 border-orange-200" },
  { value: "VAS",                label: "VAS",               badge: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  { value: "HVAS",               label: "HVAS",              badge: "bg-purple-100 text-purple-800 border-purple-200" },
  { value: "Combo EE + VAS",     label: "Combo EE + VAS",    badge: "bg-gradient-to-r from-amber-100 to-indigo-100 text-amber-900 border-amber-200" },
  { value: "Combo Gaz + VAS",    label: "Combo Gaz + VAS",   badge: "bg-gradient-to-r from-orange-100 to-indigo-100 text-orange-900 border-orange-200" },
];

// Iter52 P14h — normalize legacy lowercase/snake_case values to canonical long form.
const _KIND_ALIAS = {
  ee: "EE", gaz: "Gaz", vas: "VAS", hvas: "HVAS",
  combo_ee_vas: "Combo EE + VAS", combo_gaz_vas: "Combo Gaz + VAS",
};
export const normalizeProductKind = (val) => {
  if (!val) return null;
  const trimmed = String(val).trim();
  // Already canonical
  if (PRODUCT_KIND_OPTIONS.some((o) => o.value === trimmed)) return trimmed;
  const lower = trimmed.toLowerCase();
  if (_KIND_ALIAS[lower]) return _KIND_ALIAS[lower];
  if (lower === "combo ee + vas") return "Combo EE + VAS";
  if (lower === "combo gaz + vas") return "Combo Gaz + VAS";
  return trimmed;
};
const PRODUCT_KIND_BY_VAL = Object.fromEntries(PRODUCT_KIND_OPTIONS.map((o) => [o.value, o]));

// =========================== status pill helpers ===========================
const statusBadge = (status) => {
  const s = (status || "").toLowerCase();
  if (["activated", "activat"].includes(s)) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (["signed", "semnat"].includes(s)) return "bg-blue-50 text-blue-700 border-blue-200";
  if (["terminated", "anulat", "ko"].includes(s)) return "bg-red-50 text-red-700 border-red-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

// Iter53 P30/P32 — Supplier Status + Notes BO atoms mutate în SaleOverview/atoms.jsx
// (SUPPLIER_STATUS_OPTIONS, NotesBoInline) — importate la începutul fișierului.
// =================================================================

// =========================== main component ===========================
const SaleOverview = ({ sale, onChange, canEdit = true, flac = null }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState(searchParams.get("edit") === "1");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");

  // FLAC helper — defaults to permissive when flac not passed (e.g. admin or no enforcement)
  const cw = (key) => (flac && typeof flac.canWrite === "function" ? flac.canWrite(key) : true);

  // Sync ?edit=1 → isEditing (e.g. from external "Editează" links on Sales list)
  useEffect(() => {
    if (searchParams.get("edit") === "1" && !isEditing) {
      setIsEditing(true);
    }
  }, [searchParams, isEditing]);

  const toggleEdit = (on) => {
    setIsEditing(on);
    const next = new URLSearchParams(searchParams);
    if (on) next.set("edit", "1"); else next.delete("edit");
    setSearchParams(next, { replace: true });
  };

  // Init draft when entering edit mode
  useEffect(() => {
    if (!isEditing || !sale) return;
    const c = sale.contract || {};
    const cs = sale.contract_status || {};
    const cl = sale.client || {};
    setDraft({
      // client
      county: cl.county || "",
      locality: cl.locality || "",
      street: cl.street || "",
      postal_code: cl.postal_code || "",
      phone_mobile: cl.phone_mobile || "",
      phone_fixed: cl.phone_fixed || "",
      email: cl.email || "",
      // contract
      contract_number: c.contract_number || "",
      pod: c.pod || "",
      pod_secondary_id: c.pod_secondary_id || "",
      pdr: c.pdr || "",
      numar_loc_consum: c.numar_loc_consum || "",
      tip_loc_consum: c.tip_loc_consum || "",
      product_name: c.product_name || c.raw_product_name || c.commercial_product_name || "",
      product_kind: c.product_kind || "",
      contract_type: c.contract_type || "",
      previous_supplier: c.previous_supplier || "",
      distributor_name: c.distributor_name || "",
      // Consum address — 4 fields (county / locality / street / type)
      consumption_county: c.consumption_county || "",
      consumption_locality: c.consumption_locality || "",
      consumption_street: c.consumption_street || "",
      offer_type: c.offer_type || "",
      // SF business status (signed | activated | terminated | "")
      business_status: c.business_status || "",
      // sale
      sales_unit_external_name: sale.sales_unit_external_name || "",
      channel: sale.channel || "",
      commercial_agent_id: sale.commercial_agent_id || "",
      // Iter52 P2 — QC intern (manual / CRM legacy)
      qc_internal_outcome: sale.qc_internal_outcome || "",
      // contract_status (Quality Call card)
      cs_quality_call_outcome: cs.quality_call_outcome || "",
      cs_quality_call_date: cs.quality_call_date ? String(cs.quality_call_date).slice(0, 10) : "",
      cs_cancellation_reason: cs.cancellation_reason || "",
      cs_cancellation_details: cs.cancellation_details || "",
    });
  }, [isEditing, sale]);

  const handleSave = async () => {
    if (!sale) return;
    setSaving(true);
    try {
      const c = sale.contract || {};
      const cs = sale.contract_status || {};
      const cl = sale.client || {};

      // 1) Contract update (if any contract field changed)
      const contractDiff = {};
      const contractMap = {
        contract_number: c.contract_number,
        pod: c.pod,
        product_name: c.product_name,
        product_kind: c.product_kind,
        contract_type: c.contract_type,
        previous_supplier: c.previous_supplier,
        distributor_name: c.distributor_name,
        consumption_county: c.consumption_county,
        consumption_locality: c.consumption_locality,
        consumption_street: c.consumption_street,
        numar_loc_consum: c.numar_loc_consum,
        tip_loc_consum: c.tip_loc_consum,
        offer_type: c.offer_type,
        business_status: c.business_status,
      };
      Object.entries(contractMap).forEach(([k, oldV]) => {
        const newV = (draft[k] ?? "").trim();
        const old = (oldV ?? "").trim();
        if (newV !== old) contractDiff[k] = newV || null;
      });
      if (Object.keys(contractDiff).length && sale.contract_id) {
        const safe = flac && typeof flac.stripNonWritable === "function"
          ? flac.stripNonWritable(contractDiff, "contract")
          : contractDiff;
        if (Object.keys(safe).length) await axios.put(`${API_URL}/contracts/${sale.contract_id}`, safe);
      }

      // 2) ContractStatus update (Quality Call card)
      const csDiff = {};
      const csMap = {
        quality_call_outcome: cs.quality_call_outcome,
        cancellation_reason: cs.cancellation_reason,
        cancellation_details: cs.cancellation_details,
      };
      Object.entries(csMap).forEach(([k, oldV]) => {
        const fkey = `cs_${k}`;
        const newV = (draft[fkey] ?? "").trim();
        const old = (oldV ?? "").trim();
        if (newV !== old) csDiff[k] = newV || null;
      });
      const qcDateOld = cs.quality_call_date ? String(cs.quality_call_date).slice(0, 10) : "";
      if ((draft.cs_quality_call_date || "") !== qcDateOld) {
        csDiff.quality_call_date = draft.cs_quality_call_date || null;
      }
      if (Object.keys(csDiff).length && sale.contract_id) {
        try {
          await axios.put(`${API_URL}/contracts/${sale.contract_id}/status`, csDiff);
        } catch (err) {
          console.warn("ContractStatus update", err);
        }
      }

      // 3) Sale-level update (channel, sales_unit_external_name, QC intern)
      const saleDiff = {};
      if ((draft.channel || "") !== (sale.channel || "")) saleDiff.channel = draft.channel || null;
      if ((draft.sales_unit_external_name || "") !== (sale.sales_unit_external_name || "")) {
        saleDiff.sales_unit_external_name = draft.sales_unit_external_name || null;
      }
      // Iter51 Etapa 15 — agent reassignment (backend syncs agent_name/agent_id/agency)
      if ((draft.commercial_agent_id || "") !== (sale.commercial_agent_id || "")) {
        saleDiff.commercial_agent_id = draft.commercial_agent_id || null;
      }
      // Iter52 P2 — QC intern (CRM/manual) — distinct from QC PPC
      if (draft.qc_internal_outcome !== undefined &&
          (draft.qc_internal_outcome || "") !== (sale.qc_internal_outcome || "")) {
        saleDiff.qc_internal_outcome = draft.qc_internal_outcome || null;
      }
      if (Object.keys(saleDiff).length) {
        const safe = flac && typeof flac.stripNonWritable === "function"
          ? flac.stripNonWritable(saleDiff, "sale")
          : saleDiff;
        if (Object.keys(safe).length) await axios.put(`${API_URL}/sales/${sale.id}`, safe);
      }

      // 4) Client diff — address + contact fields
      if (cl.id) {
        const clientDiff = {};
        const clientMap = {
          county: cl.county,
          locality: cl.locality,
          street: cl.street,
          postal_code: cl.postal_code,
          phone_mobile: cl.phone_mobile,
          phone_fixed: cl.phone_fixed,
          email: cl.email,
        };
        Object.entries(clientMap).forEach(([k, oldV]) => {
          const newV = (draft[k] ?? "").trim();
          const old = (oldV ?? "").trim();
          if (newV !== old) clientDiff[k] = newV || null;
        });
        // Strip FLAC-denied fields (returns identity when no FLAC enforcement)
        const safeClient = flac && typeof flac.stripNonWritable === "function"
          ? flac.stripNonWritable(clientDiff, "client")
          : clientDiff;
        if (Object.keys(safeClient).length) {
          await axios.put(`${API_URL}/clients/${cl.id}`, safeClient);
        }
      }

      toast.success("Modificările au fost salvate");
      toggleEdit(false);
      onChange?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "flac_write_denied") {
        toast.error(`Câmpuri blocate de FLAC: ${detail.message || ""}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Eroare la salvare");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/sales/${sale.id}/withdraw`, { reason: withdrawReason.trim() || null });
      toast.success("Vânzare marcată ca renunțare");
      setWithdrawDialogOpen(false);
      setWithdrawReason("");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la marcarea renunțării");
    } finally {
      setSaving(false);
    }
  };

  const handleClearWithdraw = async () => {
    if (!window.confirm("Anulezi marcarea de renunțare? Vânzarea revine în flux normal.")) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/sales/${sale.id}/clear-withdraw`);
      toast.success("Marcaj de renunțare anulat");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la anulare");
    } finally {
      setSaving(false);
    }
  };

  // Iter52 P14r — `buildTimeline` is no longer used on the main overview; the
  // CrmActivityTimeline component fetches the real CRM history (audit + field
  // changes + status transitions) directly from /api/sales/{id}/activity.
  const validity = useMemo(() => computeValidity(sale, sale?.contract || {}, sale?.contract_status || {}), [sale]);

  if (!sale) return null;

  const c = sale.contract || {};
  const cs = sale.contract_status || {};
  const cl = sale.client || {};

  // Lună comisionare = luna CSD (obiectivele se raportează după CSD). Preferă câmpul stocat.
  const csdCommissionMonth = (() => {
    if (sale.commission_month) return sale.commission_month;
    return formatRoMonthYear(c.csd_date);
  })();

  // Agent real (semnatar) vs agent încărcare (raw din import). "INACTIV " = marcaj DB, îl curățăm la afișare.
  const stripInactive = (n) => (n || "").replace(/^INACTIV\s+/i, "").trim();
  const realAgentName = stripInactive(sale.commercial_agent?.full_name || sale.agent_name);
  const uploadAgentName = stripInactive(sale.raw_agent_full_name || sale.agent_name);
  const showUploadAgent = uploadAgentName && uploadAgentName.toUpperCase() !== realAgentName.toUpperCase();

  const isPF = cl.client_type === "PF";
  // Iter52 P13c — More resilient fallback chain so we never show bare CUI/CNP
  // as the displayed name. Order:
  //   PF: full_name → "last first" → CNP → fallback
  //   PJ: company_name → full_name → "last_name" (legacy CRM where Nume* held the
  //       company name before P13c fix) → CUI → fallback
  const clientName = isPF
    ? (cl.full_name || `${cl.last_name || ""} ${cl.first_name || ""}`.trim() || cl.cnp || "Client necunoscut")
    : (cl.company_name || cl.full_name || cl.last_name || cl.cui || "Client necunoscut");
  const cnpOrCui = isPF ? cl.cnp : cl.cui;
  const dossierCode = sale.crm_internal_id || (c.contract_number ? `C${c.contract_number}` : sale.id?.slice(0, 8));
  // Iter51 E29d — "Nr. cont" = SF Denumire cont: Număr cont (C000…). Dossier
  // code (D-…) is shown separately below. Fallback chain preserves backwards
  // compatibility for legacy data that lacks sf_account_number.
  const accountCode = cl.sf_account_number || sale.crm_internal_id || c.contract_number || sale.sf_id || sale.id?.slice(0, 8);

  const commodityLabel = c.commodity === "electric" ? "Electric" : c.commodity === "gas" ? "Gaz" : (c.commodity || "—");
  const CommodityIcon = c.commodity === "gas" ? Flame : Zap;

  // Iter52 P13i — `business_status` is the single source of truth. We display
  // the Romanian-canonical label directly (no more concatenation with the
  // legacy `internal_status` which is now derived).
  const BUSINESS_STATUS_LABEL_RO = {
    draft: "Draft",
    signed: "Semnat",
    submitted: "Trimis",
    processing: "În procesare",
    validated: "Validat",
    activated: "Activat",
    terminated: "Terminat",
    cancelled: "Anulat",
    rejected: "KO",
    withdrawn: "Renunțare",
  };
  const businessStatusText = (() => {
    const bs = (c.business_status || "").toLowerCase().trim();
    return BUSINESS_STATUS_LABEL_RO[bs] || (cs.internal_status || "—");
  })();

  const channelText = sale.channel || "—";
  const channelSub = sale.source_channel_raw || sale.sales_origin || "";

  // Editor builders
  const TextEditor = (field, placeholder = "") => (
    <Input
      value={draft[field] || ""}
      onChange={(e) => setDraft({ ...draft, [field]: e.target.value })}
      placeholder={placeholder}
      className="h-8 text-sm"
      data-testid={`edit-${field}`}
    />
  );
  const SelectEditor = (field, options) => (
    <Select value={draft[field] || ""} onValueChange={(v) => setDraft({ ...draft, [field]: v })}>
      <SelectTrigger className="h-8 text-sm" data-testid={`edit-${field}`}><SelectValue placeholder="Alege..." /></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value || o} value={o.value || o}>{o.label || o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-3" data-testid="sale-overview">
      {/* ===== 1. Breadcrumb — Iter52 P14w: derivă agenția din sale.agency.name
            (sursa autoritativă) cu fallback la `sales_unit_external_name` (raw SF). */}
      <nav className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="sale-breadcrumb">
        <Link to="/sales" className="hover:text-slate-700">Cazuri</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="hover:text-slate-700" data-testid="breadcrumb-agency">
          {sale.agency?.name || sale.contract?.agency || sale.sales_unit_external_name || "Agenție necunoscută"}
        </span>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-900 font-medium">{dossierCode}</span>
      </nav>

      {/* ===== 2. Hero header ===== */}
      <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="sale-hero">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-14 w-14 rounded-full bg-blue-50 flex items-center justify-center text-blue-700 text-base font-semibold flex-shrink-0">
            {initials(clientName)}
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-slate-900 truncate" data-testid="sale-hero-name">{clientName}</h1>
            <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5 flex-wrap">
              {cnpOrCui && (
                <>
                  <IdCard className="h-3.5 w-3.5" />
                  <span>{isPF ? "CNP" : "CUI"} {cnpOrCui}</span>
                </>
              )}
              {(cl.county || c.consumption_county) && (
                <>
                  <span className="text-slate-300">·</span>
                  <MapPin className="h-3.5 w-3.5" />
                  <span>{cl.county || c.consumption_county}</span>
                </>
              )}
              {accountCode && (
                <>
                  <span className="text-slate-300">·</span>
                  <span>Cont {accountCode}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="sale-hero-actions">
          {!isEditing ? (
            <>
              {canEdit && (
                <Button variant="outline" size="sm" onClick={() => toggleEdit(true)} data-testid="overview-edit-btn">
                  <Edit className="h-3.5 w-3.5 mr-1.5" />
                  Editează
                </Button>
              )}
              {cl.phone_mobile && (
                <Button size="sm" asChild data-testid="overview-contact-btn">
                  <a href={`tel:${cl.phone_mobile}`}>
                    <Phone className="h-3.5 w-3.5 mr-1.5" />
                    Contactează
                  </a>
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => toggleEdit(false)} disabled={saving} data-testid="overview-cancel-btn">
                <X className="h-3.5 w-3.5 mr-1.5" />
                Anulează
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="overview-save-btn">
                <Save className="h-3.5 w-3.5 mr-1.5" />
                {saving ? "Se salvează..." : "Salvează"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ===== 2.5 Withdrawal banner ===== */}
      {sale.is_withdrawn && (
        <Card className="border-2 border-red-300 bg-red-50" data-testid="withdrawn-banner">
          <CardContent className="p-3 flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <Ban className="h-4 w-4 text-red-700 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-red-900">Vânzare marcată ca renunțare</p>
                {sale.withdrawn_reason && <p className="text-red-800 mt-0.5">Motiv: {sale.withdrawn_reason}</p>}
                {sale.withdrawn_at && <p className="text-xs text-red-700 mt-0.5">{fmt.datetime(sale.withdrawn_at)}</p>}
              </div>
            </div>
            <Button size="sm" variant="outline" className="border-red-300 text-red-800 hover:bg-red-100" onClick={handleClearWithdraw} disabled={saving} data-testid="clear-withdraw-btn">
              <Undo2 className="h-3.5 w-3.5 mr-1" /> Anulează marcaj
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ===== 2.6 INVALID SALE BANNER (any of QC / SF / Extern negative) ===== */}
      {validity.isInvalid && (
        <Card className="border-2 border-red-400 bg-gradient-to-r from-red-50 to-orange-50" data-testid="invalid-sale-banner">
          <CardContent className="p-3 flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-900">Vânzare invalidă</p>
              <p className="text-xs text-red-800 mt-0.5">
                Cel puțin un status critic indică o stare negativă. Verifică detaliile mai jos:
              </p>
              <ul className="mt-1.5 space-y-0.5">
                {validity.reasons.map((r, i) => (
                  <li key={i} className="text-xs text-red-900 flex items-start gap-1.5" data-testid={`invalid-reason-${i}`}>
                    <span className="text-red-500 mt-0.5">•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== 2.7 FOUR VALIDITY STATUS CARDS (QC PPC / QC INTERN / SF / EXTERN) ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="validity-cards-row">
        <StatusValidityCard
          title="QC PPC (Salesforce)"
          label={validity.qc.label}
          state={validity.qc.state}
          helper={qcHelper(validity.qc.label !== "Neevaluat" ? validity.qc.label : null, cs.quality_call_date || sale.qc_call_date)}
          testId="status-card-qc"
          action={isEditing && canEdit && cw("sale.quality_call_outcome") ? (
            <Select value={draft.cs_quality_call_outcome || ""} onValueChange={(v) => setDraft({ ...draft, cs_quality_call_outcome: v })}>
              <SelectTrigger className="h-7 text-xs bg-white" data-testid="edit-qc-outcome-card"><SelectValue placeholder="Alege outcome..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Confirmă contractul">Confirmă contractul</SelectItem>
                <SelectItem value="Nu confirmă">Nu confirmă</SelectItem>
                <SelectItem value="Reapelare">Reapelare</SelectItem>
                <SelectItem value="Refuzat">Refuzat</SelectItem>
                <SelectItem value="În așteptare">În așteptare</SelectItem>
              </SelectContent>
            </Select>
          ) : null}
        />
        {/* Iter69h — QC AQS (DISTINCT de QC PPC / QC Intern). Apare doar dacă dosarul AQS a trimis QC (sale.aqs_qc). */}
        {sale.aqs_qc && (
          <StatusValidityCard
            title="QC AQS"
            titleHint="Quality Call din sursa AQS — DISTINCT de QC PPC (Salesforce) și de QC Intern. «sent»/succesul transmiterii NU înseamnă QC pozitiv; responsabilul este cel din sursă, nu dovada cine a efectuat apelul."
            label={sale.aqs_qc.outcome_normalized === "qc_ok" ? "OK" : sale.aqs_qc.outcome_normalized === "qc_ko" ? "KO" : "Necunoscut"}
            state={sale.aqs_qc.outcome_normalized === "qc_ok" ? "positive" : sale.aqs_qc.outcome_normalized === "qc_ko" ? "negative" : "neutral"}
            badgeLabel={sale.aqs_qc.outcome_normalized === "qc_ok" ? "OK" : sale.aqs_qc.outcome_normalized === "qc_ko" ? "KO" : "NECUNOSCUT"}
            helper={`Responsabil în sursă: ${sale.aqs_qc.operator_source || "neprecizat"} · ${sale.aqs_qc.call_date ? `data apelului: ${fmt.date(sale.aqs_qc.call_date)}` : "data apelului: neînregistrată"} · brut „${sale.aqs_qc.outcome_raw || "—"}"/„${sale.aqs_qc.status_raw || "—"}" · sursă ${sale.aqs_qc.source} · rev ${sale.aqs_qc.dossier_revision}`}
            testId="status-card-qc-aqs"
          />
        )}
        {/* Iter52 P2 — QC Intern (CRM ops; SF importer never writes here) */}
        <StatusValidityCard
          title="QC Intern (CRM)"
          label={sale.qc_internal_outcome || "—"}
          state={sale.qc_internal_outcome ? "ok" : "pending"}
          helper={sale.qc_internal_date ? `Înregistrat la ${fmt.date(sale.qc_internal_date)}` : "QC intern neînregistrat"}
          testId="status-card-qc-internal"
          action={isEditing && canEdit && cw("sale.qc_internal_outcome") ? (
            <Select
              value={draft.qc_internal_outcome ?? sale.qc_internal_outcome ?? ""}
              onValueChange={(v) => setDraft({ ...draft, qc_internal_outcome: v })}
            >
              <SelectTrigger className="h-7 text-xs bg-white" data-testid="edit-qc-internal-outcome">
                <SelectValue placeholder="Alege outcome..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="OK">OK</SelectItem>
                <SelectItem value="KO">KO</SelectItem>
                <SelectItem value="Reapelare">Reapelare</SelectItem>
                <SelectItem value="În verificare">În verificare</SelectItem>
                <SelectItem value="Refuzat">Refuzat</SelectItem>
              </SelectContent>
            </Select>
          ) : null}
        />
        <StatusValidityCard
          title="Status SF"
          titleHint="Stare contract raportată de Salesforce (col. „Stare contract” din import SF). Autoritativ pentru semnare/activare/anulare."
          label={validity.sf.label}
          state={validity.sf.state}
          helper={validity.sf.label && validity.sf.label !== "Nedefinit"
            ? `Sursă: Salesforce (col V — Stare contract)`
            : "Niciun status SF importat pentru acest dosar"}
          testId="status-card-sf"
          /* Iter52 P13i — Status SF este READ-ONLY (oglindă a fișierului SF).
             Nu mai expunem editor inline aici — modificările vin doar prin import SF.
             Pentru CRM workflow intern se folosește cardul "Status dosar (CRM)" de mai jos. */
        />
        <StatusValidityCard
          title="Renunțare (marcaj manual)"
          titleHint="Marcaj manual binar pe vânzare (sale.is_withdrawn). Nu este statusul furnizorului și nu confirmă furnizarea activă."
          label={validity.ext.label}
          state={validity.ext.state}
          badgeLabel={validity.ext.badge}
          helper={sale.is_withdrawn && sale.withdrawn_at ? `Marcat la ${fmt.datetime(sale.withdrawn_at)}` : "Nicio renunțare marcată manual — nu înseamnă furnizare activă (vezi Status SF / Status furnizor)."}
          testId="status-card-extern"
          action={canEdit ? (
            sale.is_withdrawn ? (
              <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-800 hover:bg-red-100 w-full" onClick={handleClearWithdraw} disabled={saving} data-testid="status-card-clear-withdraw">
                <Undo2 className="h-3 w-3 mr-1" /> Anulează renunțare
              </Button>
            ) : (
              <Button size="sm" variant="ghost" className="h-7 text-xs text-red-700 hover:bg-red-50 w-full" onClick={() => setWithdrawDialogOpen(true)} disabled={saving} data-testid="status-card-mark-withdraw">
                <Ban className="h-3 w-3 mr-1" /> Marchează renunțare
              </Button>
            )
          ) : null}
        />
      </div>

      {/* ===== 3. Status pills (4 metric cards) ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sale-status-pills">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Status dosar</p>
            <Badge className={`${statusBadge(c.business_status)} border mt-1.5 text-xs px-2 py-0.5`} data-testid="pill-business-status">
              {businessStatusText}
            </Badge>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Commodity</p>
            <div className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-900" data-testid="pill-commodity">
              <CommodityIcon className="h-4 w-4 text-[#0C447C]" />
              {commodityLabel}
            </div>
          </CardContent>
        </Card>
        {/* Iter52 P13d — Tip proces (SF col J + col K) + commissionability hint */}
        <Card className="border-slate-200 shadow-sm" data-testid="pill-registration-type">
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Tip proces</p>
            {sale.case_record_type ? (
              <>
                <div className="mt-1.5 flex items-start gap-1.5">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
                      sale.is_commissionable
                        ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                        : "bg-slate-100 text-slate-700 border-slate-300"
                    }`}
                    data-testid="badge-record-type"
                    title={sale.is_commissionable
                      ? "Proces comisionabil"
                      : "Proces ne-comisionabil (schimbare ofertă / transfer titular)"}
                  >
                    {sale.is_commissionable ? "💰" : "⊘"} {sale.case_record_type}
                  </span>
                </div>
                {sale.case_process_type && (
                  <p
                    className="text-[11px] text-slate-500 mt-1 truncate"
                    title={sale.case_process_type}
                    data-testid="badge-process-type"
                  >
                    {sale.case_process_type}
                  </p>
                )}
              </>
            ) : (
              <p className="mt-1.5 text-sm text-slate-400 italic">—</p>
            )}
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Canal vânzare</p>
            <div className="mt-1.5 text-sm font-semibold text-slate-900" data-testid="pill-channel">{channelText}</div>
            {channelSub && <p className="text-[11px] text-slate-400 mt-0.5">{channelSub}</p>}
          </CardContent>
        </Card>
      </div>

      {/* ===== 3b. Iter53 P27/P30 — Status furnizor + Note backoffice mutate
            din card-ul separat în secțiunea „Detalii contract" (la sfârșit). ===== */}

      {/* ===== 4. Two-col: Date client + Detalii contract ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SectionCard icon={User} title="Date client" testId="card-client">
          <FieldRow icon={IdCard} label={isPF ? "Cheie / CNP" : "Cheie / CUI"} value={cnpOrCui ? <CopyableValue value={cnpOrCui} mono label={isPF ? "CNP" : "CUI"} /> : null} mono />
          <FieldRow icon={FileText} label="Nr. cont" value={accountCode ? <CopyableValue value={accountCode} mono label="Nr. cont" /> : null} />
          <FieldRow icon={Home} label="Tip client" value={isPF ? "Residential" : (cl.client_type === "PJ" ? "Business" : "—")} />
          {/* Contact */}
          <FieldRow
            icon={Phone}
            label="Telefon mobil"
            value={cl.phone_mobile}
            isEditing={isEditing && cw("client.phone_mobile")}
            editor={TextEditor("phone_mobile", "07xx xxx xxx")}
          />
          <FieldRow
            icon={Phone}
            label="Telefon fix"
            value={cl.phone_fixed}
            isEditing={isEditing && cw("client.phone_fixed")}
            editor={TextEditor("phone_fixed", "0xx xxx xxxx")}
          />
          <FieldRow
            icon={FileSignature}
            label="Email"
            value={cl.email}
            isEditing={isEditing && cw("client.email")}
            editor={TextEditor("email", "client@example.ro")}
          />
          {/* Address */}
          <FieldRow
            icon={MapPin}
            label="Județ"
            value={cl.county}
            isEditing={isEditing && cw("client.county")}
            editor={TextEditor("county", "ex. Dolj")}
          />
          <FieldRow
            icon={Building}
            label="Localitate"
            value={cl.locality}
            isEditing={isEditing && cw("client.locality")}
            editor={TextEditor("locality", "ex. Craiova")}
          />
          <FieldRow
            icon={RouteIcon}
            label="Stradă, nr."
            value={cl.street}
            isEditing={isEditing && cw("client.street")}
            editor={TextEditor("street", "ex. Str. Mihai Eminescu 12")}
          />
          <FieldRow
            icon={Hash}
            label="Cod poștal"
            value={cl.postal_code}
            mono
            isEditing={isEditing && cw("client.postal_code")}
            editor={TextEditor("postal_code", "200xxx")}
          />
          {/* Supplier history (contract-level, but contextually relevant) */}
          <FieldRow
            icon={Building}
            label="Furnizor anterior"
            value={c.previous_supplier}
            isEditing={isEditing && cw("contract.previous_supplier")}
            editor={
              <SupplierCombobox
                value={draft.previous_supplier || ""}
                onChange={(v) => setDraft({ ...draft, previous_supplier: v })}
                commodity={c.commodity}
                placeholder="Alege furnizor anterior..."
                testId="edit-previous_supplier"
              />
            }
          />
          <FieldRow
            icon={Network}
            label="Distribuitor"
            value={c.distributor_name}
            isEditing={isEditing && cw("contract.distributor_name")}
            editor={SelectEditor("distributor_name", DISTRIBUTORS.map((s) => ({ value: s, label: s })))}
          />
        </SectionCard>

        <SectionCard icon={FileText} title="Detalii contract" testId="card-contract">
          <FieldRow
            icon={Hash}
            label="Nr. contract (real, VI…)"
            value={c.contract_number ? <CopyableValue value={c.contract_number} mono label="Nr. contract" /> : null}
            mono
            isEditing={isEditing && cw("contract.contract_number")}
            editor={TextEditor("contract_number")}
          />
          {/* Iter52 — Per-source contract identifiers (always visible so users
              see the 3-way split even when a value is missing). Source is
              encoded in the row label itself (no extra badge needed). */}
          <FieldRow
            icon={Hash}
            label="ID contract SF (Salesforce)"
            value={c.sf_contract_id ? <CopyableValue value={c.sf_contract_id} mono label="ID SF" /> : null}
            mono
          />
          <FieldRow
            icon={Hash}
            label="ID contract Easy (Easy App)"
            value={c.easy_contract_id ? <CopyableValue value={c.easy_contract_id} mono label="ID Easy" /> : null}
            mono
          />
          <FieldRow icon={Hash} label="Nr. caz" value={(sale.sf_id || dossierCode) ? <CopyableValue value={sale.sf_id || dossierCode} mono label="Nr. caz" /> : null} mono />
          <FieldRow
            icon={Tag}
            label="Produs"
            badge={!isEditing && (c.product_name || c.raw_product_name) ? (
              <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-xs">{c.product_name || c.raw_product_name}</Badge>
            ) : null}
            value={c.product_name || c.raw_product_name}
            isEditing={isEditing && cw("contract.product_name")}
            editor={TextEditor("product_name", "ex. PPC Simplu Senior")}
          />
          <FieldRow
            icon={Tag}
            label="Tip produs"
            badge={!isEditing && c.product_kind ? (
              <Badge className={`${PRODUCT_KIND_BY_VAL[normalizeProductKind(c.product_kind)]?.badge || "bg-slate-100 text-slate-700"} border text-xs`} data-testid="product-kind-badge">
                {PRODUCT_KIND_BY_VAL[normalizeProductKind(c.product_kind)]?.label || c.product_kind}
              </Badge>
            ) : null}
            value={c.product_kind ? (PRODUCT_KIND_BY_VAL[normalizeProductKind(c.product_kind)]?.label || c.product_kind) : null}
            isEditing={isEditing && cw("contract.product_kind")}
            editor={
              <Select value={normalizeProductKind(draft.product_kind) || ""} onValueChange={(v) => setDraft({ ...draft, product_kind: v })}>
                <SelectTrigger className="h-8 text-sm" data-testid="edit-product_kind"><SelectValue placeholder="Alege tip..." /></SelectTrigger>
                <SelectContent>
                  {PRODUCT_KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
          {/* Iter52 P14u — Câmp "Tip înregistrare" eliminat: era mapat pe
              `contract.contract_type` (mereu NULL în DB) și DUPLICA badge-ul
              "Tip Proces" din header (care folosește `case_record_type` din
              ProcessingCase). Single source of truth: case_record_type. */}
          <FieldRow icon={Plug} label="POD" value={c.pod ? <CopyableValue value={c.pod} mono label="POD" testId={`pod-${c.pod}`} /> : null} mono isEditing={isEditing && cw("contract.pod")} editor={TextEditor("pod")} />
          {/* Agent & Agenție (replaces separate "Echipa de vânzare" card) */}
          <FieldRow
            icon={User}
            label="Agent real (semnatar)"
            badge={!isEditing && sale.commercial_agent_id ? (
              <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs">
                {realAgentName}
              </Badge>
            ) : (!isEditing && (sale.raw_agent_full_name || sale.agent_name)) ? (
              // Iter52 P17 — agent prezent ca text raw din SF dar nelegat la user CRM
              <span className="inline-flex items-center gap-1.5">
                <Badge className="bg-slate-50 text-slate-700 border border-slate-200 text-xs">
                  {realAgentName}
                </Badge>
                <Badge
                  className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px] cursor-help"
                  title={sale.reconciliation_note || "Codul de agent din import nu corespunde unui user CRM. Asignează codul agent în Users → Agenți și apoi reconciliază."}
                  data-testid="agent-unreconciled-badge"
                >
                  ⚠ neasociat
                </Badge>
              </span>
            ) : null}
            value={realAgentName}
            isEditing={isEditing && cw("sale.commercial_agent_id")}
            editor={
              <AgentCombobox
                value={draft.commercial_agent_id || ""}
                currentLabel={realAgentName}
                onChange={(agentId) => setDraft({ ...draft, commercial_agent_id: agentId })}
                placeholder="Alege agent..."
                testId="edit-commercial_agent_id"
              />
            }
          />
          {!isEditing && showUploadAgent && (
            <FieldRow
              icon={User}
              label="Agent încărcare"
              value={uploadAgentName}
              testId="overview-upload-agent"
            />
          )}
          <FieldRow
            icon={Building}
            label="Agenție"
            value={sale.agency?.name || c.agency || sale.sales_unit_external_name}
          />
          <FieldRow
            icon={Store}
            label="Sales unit (extern)"
            value={sale.sales_unit_external_name}
            isEditing={isEditing && cw("sale.sales_unit_external_name")}
            editor={TextEditor("sales_unit_external_name")}
          />
          {/* Iter51 Etapa 14b — Loc consum (adresă & identificatori) consolidat aici */}
          <div className="pt-2 mt-1 border-t border-slate-100">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">Loc consum</p>
          </div>
          <FieldRow
            icon={MapPin}
            label="Județ consum"
            value={c.consumption_county}
            isEditing={isEditing && cw("contract.consumption_county")}
            editor={TextEditor("consumption_county", "ex. Dolj")}
          />
          <FieldRow
            icon={Building}
            label="Localitate consum"
            value={c.consumption_locality}
            isEditing={isEditing && cw("contract.consumption_locality")}
            editor={TextEditor("consumption_locality", "ex. Craiova")}
          />
          <FieldRow
            icon={RouteIcon}
            label="Stradă, nr., bloc"
            value={c.consumption_street}
            isEditing={isEditing && cw("contract.consumption_street")}
            editor={TextEditor("consumption_street", "ex. Str. Mihai Eminescu 12, Bl. A3")}
          />
          <FieldRow
            icon={Hash}
            label="Nr. loc consum"
            value={c.numar_loc_consum}
            mono
            isEditing={isEditing && cw("contract.numar_loc_consum")}
            editor={TextEditor("numar_loc_consum")}
          />
          <FieldRow
            icon={Tag}
            label="Tip loc consum"
            value={c.tip_loc_consum}
            isEditing={isEditing && cw("contract.tip_loc_consum")}
            editor={TextEditor("tip_loc_consum", "ex. Casnic / Non-casnic")}
          />
          <FieldRow
            icon={Plug}
            label="PDR (gaz)"
            value={c.pdr}
            mono
            isEditing={isEditing && cw("contract.pdr")}
            editor={TextEditor("pdr")}
          />

          {/* Iter53 P27/P30 — Status furnizor + Note backoffice (mutate aici din card-ul separat).
              Editabile DOAR pentru admin/BO. */}
          <div className="pt-3 mt-3 border-t border-slate-200 space-y-3" data-testid="contract-supplier-status-section">
            <div className="flex items-start justify-between gap-3">
              <span className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap min-w-fit pt-1.5">
                <Zap className="h-3.5 w-3.5 text-indigo-500" />
                Status furnizor
              </span>
              <div className="flex-1 max-w-[260px]">
                {canEdit ? (
                  <select
                    value={c.supplier_status || ""}
                    onChange={async (e) => {
                      const next = e.target.value || null;
                      try {
                        await axios.put(
                          `${API_URL}/contracts/${c.id}/supplier-fields`,
                          { supplier_status: next, fields_to_patch: ["supplier_status"], reason: "inline edit overview" }
                        );
                        toast.success(next ? `Status furnizor: ${next}` : "Status furnizor șters");
                        if (onChange) await onChange();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Eroare la salvare status");
                      }
                    }}
                    className="w-full text-sm border rounded-md p-1.5 bg-white"
                    data-testid="overview-supplier-status-select"
                  >
                    {SUPPLIER_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <span className="text-sm font-medium text-slate-900 text-right block" data-testid="overview-supplier-status-value">
                    {c.supplier_status || <EmptyValue />}
                  </span>
                )}
                {c.supplier_status_date && (
                  <p className="text-[10px] text-slate-400 mt-1 text-right">
                    Setat: {new Date(c.supplier_status_date).toLocaleString("ro-RO")}
                  </p>
                )}
              </div>
            </div>

            <div data-testid="contract-notes-bo-section">
              <span className="flex items-center gap-1.5 text-xs text-slate-500 mb-1.5">
                <FileText className="h-3.5 w-3.5 text-slate-400" />
                Note backoffice
              </span>
              {canEdit ? (
                <NotesBoInline sale={sale} onSaved={onChange} />
              ) : (
                <p className="text-sm text-slate-900 whitespace-pre-wrap rounded-md border border-dashed border-slate-200 p-2" data-testid="overview-notes-bo-value-readonly">
                  {sale?.notes_bo || <span className="text-slate-400 italic">— fără note —</span>}
                </p>
              )}
            </div>
          </div>
        </SectionCard>
      </div>

      {/* ===== 4.5. (Card „Loc consum" eliminat — câmpurile consolidate în Detalii contract) ===== */}

      {/* ===== 5. (Echipa de vânzare card eliminat — Agent + Agenție mutate în Detalii contract) ===== */}

      {/* ===== 6. Timeline vânzare — Iter52 P14r: istoric CRM real (audit + field changes + status) ===== */}
      <SectionCard icon={Workflow} title="Timeline vânzare" testId="card-timeline">
        <CrmActivityTimeline saleId={sale?.id} />
      </SectionCard>

      {/* ===== 7. Two-col: Quality Call + Detalii proces ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SectionCard icon={PhoneCall} title="Quality call" testId="card-qc">
          <FieldRow
            label="Outcome"
            badge={!isEditing && (cs.quality_call_outcome || sale.quality_call_outcome) ? (
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                {cs.quality_call_outcome || sale.quality_call_outcome}
              </Badge>
            ) : null}
            value={cs.quality_call_outcome || sale.quality_call_outcome}
            isEditing={isEditing}
            editor={SelectEditor("cs_quality_call_outcome", [
              { value: "Confirmă contractul", label: "Confirmă contractul" },
              { value: "Nu confirmă", label: "Nu confirmă" },
              { value: "Reapelare", label: "Reapelare" },
              { value: "Refuzat", label: "Refuzat" },
              { value: "În așteptare", label: "În așteptare" },
            ])}
          />
          <FieldRow
            label="Data confirmării"
            value={fmt.date(cs.quality_call_date || sale.qc_call_date)}
            isEditing={isEditing}
            editor={<Input type="date" value={draft.cs_quality_call_date || ""} onChange={(e) => setDraft({ ...draft, cs_quality_call_date: e.target.value })} className="h-8 text-sm" data-testid="edit-cs_quality_call_date" />}
          />
          <FieldRow
            label="Motiv anulare"
            value={cs.cancellation_reason || sale.cancellation_reason_text}
            isEditing={isEditing}
            editor={TextEditor("cs_cancellation_reason")}
          />
          <FieldRow
            label="Cancellation details"
            value={cs.cancellation_details}
            isEditing={isEditing}
            editor={<Textarea rows={2} value={draft.cs_cancellation_details || ""} onChange={(e) => setDraft({ ...draft, cs_cancellation_details: e.target.value })} className="text-sm" data-testid="edit-cs_cancellation_details" />}
          />
        </SectionCard>

        <SectionCard icon={Settings} title="Detalii proces" testId="card-process">
          <FieldRow label="Tip proces" value={c.offer_type || c.product_name} />
          <FieldRow label="Tip contract" value={isPF ? "Residential" : (cl.client_type === "PJ" ? "Business" : (c.contract_type || "—"))} />
          <FieldRow
            label="Stare furnizare"
            badge={c.business_status ? (
              <Badge className={`${statusBadge(c.business_status)} border text-xs`}>{c.business_status}</Badge>
            ) : null}
            value={c.business_status}
          />
          <FieldRow label="Dossier code" value={dossierCode} mono />
          {/* Iter52 P14j — SF-imported timeline fields (visible on main sale page) */}
          <FieldRow
            icon={Calendar}
            label="Data semnării contract"
            value={fmt.date(c.signed_date) || "—"}
          />
          <FieldRow
            icon={Calendar}
            label="Document Validation Date"
            value={fmt.date(sale?.document_validation_date) || "—"}
          />
          <FieldRow
            icon={Calendar}
            label="Request Confirmation Date (CSD)"
            value={fmt.date(sale?.request_confirmed_at || c.csd_date) || "—"}
          />
          <FieldRow
            icon={Calendar}
            label="Data CSD (bază obiective)"
            value={fmt.date(c.csd_date) || "—"}
            testId="overview-csd-date"
          />
          <FieldRow
            icon={Calendar}
            label="Lună comisionare (CSD)"
            badge={csdCommissionMonth ? (
              <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs" data-testid="overview-commission-month">
                {csdCommissionMonth}
              </Badge>
            ) : null}
            value={csdCommissionMonth || "—"}
          />
          <FieldRow
            icon={Users}
            label="Ultima modificare de (SF)"
            value={sale?.sf_last_modified_by_name || "—"}
          />
        </SectionCard>
      </div>

      {/* ===== 8. Withdraw dialog (rendered unconditionally — opened from Status Extern card or bottom button) ===== */}
      {!sale.is_withdrawn && (
        <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Marchează vânzarea ca renunțare</DialogTitle>
              <DialogDescription>
                Acest marcaj este singura intervenție „externă" pe status. Statusul SF (signed / activated / terminated) rămâne sursa de adevăr pentru flow-ul automat.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <label className="text-sm font-medium">Motiv (opțional, dar recomandat)</label>
              <Textarea value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} placeholder="Ex: Client a renunțat după primul Quality Call" rows={3} data-testid="withdraw-reason-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setWithdrawDialogOpen(false)} disabled={saving}>Anulează</Button>
              <Button variant="destructive" onClick={handleWithdraw} disabled={saving} data-testid="confirm-withdraw-btn">Confirm renunțare</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Bottom legacy button (only visible in view mode for backwards compat) */}
      {!sale.is_withdrawn && !isEditing && (
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" className="text-red-700 hover:bg-red-50" onClick={() => setWithdrawDialogOpen(true)} data-testid="open-withdraw-dialog-btn">
            <Ban className="h-3.5 w-3.5 mr-1.5" /> Marchează renunțare
          </Button>
        </div>
      )}

      {/* ===== 9. Bottom action buttons ===== */}
      {!isEditing && (
        <div className="flex justify-end gap-2 pt-1 flex-wrap" data-testid="sale-bottom-actions">
          {cl.id && (
            <Button variant="outline" size="sm" asChild data-testid="bottom-action-history">
              <Link to={`/clients/${cl.id}`}>
                <History className="h-3.5 w-3.5 mr-1.5" /> Istoric client
              </Link>
            </Button>
          )}
          <Button variant="outline" size="sm" asChild data-testid="bottom-action-export">
            <a href={`${API_URL}/sales/${sale.id}/export`} target="_blank" rel="noreferrer">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Exportă fișă
            </a>
          </Button>
          <Button size="sm" asChild data-testid="bottom-action-dossier">
            <a href="#tabs-actions">
              <ArrowRight className="h-3.5 w-3.5 mr-1.5" /> Acțiuni dosar
            </a>
          </Button>
        </div>
      )}
    </div>
  );
};

export default SaleOverview;
